import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, DocumentTextIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../../contexts/AuthContextLazy';
import { showWarning } from '../../../utils/toast';

type DocumentType = 'needs_analysis' | 'reason_why' | null;

export default function NewDocument() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [selectedType, setSelectedType] = useState<DocumentType>(null);
  const clientId = searchParams.get('clientId');

  const handleSelectType = (type: DocumentType) => {
    setSelectedType(type);
  };

  const handleContinue = () => {
    if (!selectedType) {
      showWarning('Please select a document type');
      return;
    }

    // Navigate to the appropriate wizard, passing clientId if present
    const url = clientId 
      ? `/portal/compliance/wizard/${selectedType}?clientId=${clientId}`
      : `/portal/compliance/wizard/${selectedType}`;
    navigate(url);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center mb-8">
          <button
            onClick={() => navigate('/portal/compliance')}
            className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Select Document Type</h1>
            <p className="text-gray-600 mt-1">Choose the type of compliance document to create</p>
          </div>
        </div>

        {/* Document Type Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Needs Analysis Card */}
          <button
            onClick={() => handleSelectType('needs_analysis')}
            className={`relative p-6 bg-white rounded-lg shadow-lg hover:shadow-xl transition-all text-left ${
              selectedType === 'needs_analysis'
                ? 'ring-2 ring-amber-500 ring-offset-2'
                : 'hover:scale-105'
            }`}
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-center w-16 h-16 bg-amber-100 rounded-lg mb-4">
                <ClipboardDocumentCheckIcon className="h-8 w-8 text-amber-600" />
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                📊 Needs Analysis
              </h3>
              
              <p className="text-gray-600 mb-4 flex-grow">
                Complete financial snapshot of client's current situation
              </p>
              
              <ul className="text-sm text-gray-500 space-y-1 mb-4">
                <li>• Required for all sales</li>
                <li>• Update existing data</li>
                <li>• ~10 minutes</li>
              </ul>
              
              <div className={`text-center py-2 px-4 rounded-lg font-medium transition-colors ${
                selectedType === 'needs_analysis'
                  ? 'bg-amber-500 text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {selectedType === 'needs_analysis' ? 'Selected ✓' : 'Select'}
              </div>
            </div>
          </button>

          {/* Reason Why Letter Card */}
          <button
            onClick={() => handleSelectType('reason_why')}
            className={`relative p-6 bg-white rounded-lg shadow-lg hover:shadow-xl transition-all text-left ${
              selectedType === 'reason_why'
                ? 'ring-2 ring-amber-500 ring-offset-2'
                : 'hover:scale-105'
            }`}
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-center w-16 h-16 bg-amber-100 rounded-lg mb-4">
                <DocumentTextIcon className="h-8 w-8 text-amber-600" />
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                📝 Reason Why Letter
              </h3>
              
              <p className="text-gray-600 mb-4 flex-grow">
                Document your product recommendation and rationale
              </p>
              
              <ul className="text-sm text-gray-500 space-y-1 mb-4">
                <li>• Required for each product sale</li>
                <li>• Links to needs analysis</li>
                <li>• ~5 minutes</li>
              </ul>
              
              <div className={`text-center py-2 px-4 rounded-lg font-medium transition-colors ${
                selectedType === 'reason_why'
                  ? 'bg-amber-500 text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {selectedType === 'reason_why' ? 'Selected ✓' : 'Select'}
              </div>
            </div>
          </button>
        </div>

        {/* Information Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-blue-900 mb-2">ℹ️ Compliance Requirements</h4>
          <p className="text-sm text-blue-800">
            Both documents are required for regulatory compliance in Saskatchewan and Alberta. 
            The Needs Analysis provides a complete financial picture, while the Reason Why Letter 
            documents your specific product recommendations.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between">
          <button
            onClick={() => navigate('/portal/compliance')}
            className="px-6 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleContinue}
            disabled={!selectedType}
            className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center ${
              selectedType
                ? 'bg-amber-500 hover:bg-amber-600 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Continue
            <ArrowLeftIcon className="h-5 w-5 ml-2 rotate-180" />
          </button>
        </div>
      </div>
  );
}